import axios from "axios";

import { isMockEnabled } from "../lib/mock";
import { loginCustomerParams } from "./google-ads";

export enum KeywordAgentStatus {
  ACTIVE = "active",
  PAUSED = "paused",
}

export enum KeywordAgentPauseReason {
  AD_GROUP_DELETED = "ad-group-deleted",
  CAMPAIGN_DELETED = "campaign-deleted",
  INTEGRATION_DISCONNECTED = "integration-disconnected",
  PAUSED_BY_USER = "paused-by-user",
}

export enum KeywordAgentTermStatus {
  IN_REVIEW = "in-review",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
}

export enum KeywordAgentTermMatchType {
  BROAD = "broad",
  EXACT = "exact",
  PHRASE = "phrase",
}

// The negative standing in the ad group that a term is blocked by. Usually
// not the term itself: the agent blocks the wording a set of bad queries
// share, so one of these covers several of them — and catches the ones
// nobody has typed yet.
export interface KeywordAgentNegative {
  text: string;
  "match-type": KeywordAgentTermMatchType;
}

export interface KeywordAgent {
  id: number;
  "workspace-id": number;
  "campaign-criteria-id": number;
  "google-ads-account": string;
  "google-ads-login-customer-id": string | null;
  "google-ads-campaign-id": number;
  "google-ads-ad-group-id": number;
  "currency-code": string;
  status: KeywordAgentStatus;
  "pause-reason": KeywordAgentPauseReason | null;
  "activation-deadline": string;
  "last-paused-at": string;
  "created-at": string;
  "updated-at": string;
}

export interface KeywordAgentTerm {
  id: number;
  "keyword-agent-id": number;
  term: string;
  // Null while nothing is blocking it, which is every term the agent
  // hasn't ruled against and every one it has ruled against but not yet
  // got a negative into Google for.
  "blocked-by": KeywordAgentNegative | null;
  status: KeywordAgentTermStatus;
  "original-status": KeywordAgentTermStatus;
  // 1–10, null until judged. Scored by the LLM, except for a keyword the
  // ad group bids on: those are kept at 10 without asking.
  "fit-score": number | null;
  // 0–10: how much the user answering for this term would improve the
  // agent's judgement of terms it hasn't seen. What the review queue is
  // ranked by.
  teach: number | null;
  reason: string | null;
  // What the agent would ask about this term, which is what the review
  // puts to the user. Null where it had nothing to ask.
  question: string | null;
  // Why the user ruled the way they did, on either verdict.
  "user-note": string | null;
  // A term that carries these is never put back in the review queue,
  // whichever way the user ruled.
  "answered-at": string | null;
  "answered-criteria-id": number | null;
  "answered-criteria-revision": number | null;
  // Whether an exclusion is standing in the ad group right now, and
  // whether one was ever taken back out.
  "excluded?": boolean;
  "restored?": boolean;
  "excluded-at": string | null;
  "restored-at": string | null;
  "metrics-start-date": string;
  "metrics-end-date": string;
  // The earliest day inside the window the term actually ran, which is
  // what monthly-cost divides its spend by.
  "first-seen-date": string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  "conversions-value": number;
  // What the term was on course to cost over a month, server-computed.
  "monthly-cost": number;
  "created-at": string;
  "updated-at": string;
}

export type KeywordAgentTermView = "review" | "excluded" | "restored";

// Naming a campaign and ad group that don't belong together answers with
// nothing rather than with the ad group's agent.
const makeEndpoint = (
  workspaceSlug: string,
  customerId: string,
  campaignId: number,
  adGroupId: number,
): string => {
  const resource = isMockEnabled() ? "mock-campaigns" : "campaigns";
  return `/api/v1/workspaces/${workspaceSlug}/integrations/google-ads/customer-accounts/${customerId}/${resource}/${campaignId}/ad-groups/${adGroupId}/agent`;
};

export const getKeywordAgents = async (
  workspaceSlug: string,
  { status }: { status?: KeywordAgentStatus } = {},
): Promise<KeywordAgent[]> => {
  const params = new URLSearchParams();
  if (status !== undefined) {
    params.set("status", status);
  }
  return (
    await axios.get(`/api/v1/workspaces/${workspaceSlug}/keyword-agents`, {
      params,
    })
  ).data;
};

export const getKeywordAgent = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
): Promise<KeywordAgent> => {
  return (
    await axios.get(
      makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId),
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

// Also resumes a paused agent: its row is reused, so the terms it has
// judged and the exclusions it holds survive.
export const startKeywordAgent = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
  campaignCriteriaId: number,
): Promise<KeywordAgent> => {
  return (
    await axios.post(
      makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId),
      { "campaign-criteria-id": campaignCriteriaId },
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

// Points the agent at a different criteria, leaving it running or paused
// as it was.
export const patchKeywordAgent = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
  campaignCriteriaId: number,
): Promise<KeywordAgent> => {
  return (
    await axios.patch(
      makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId),
      { "campaign-criteria-id": campaignCriteriaId },
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

// Takes the agent's exclusions back out of the ad group before it stops.
// Answers with how many it couldn't remove, which is not always zero.
export const pauseKeywordAgent = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
): Promise<{ "exclusions-remaining": number }> => {
  return (
    await axios.delete(
      makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId),
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

export const getKeywordAgentTerms = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
  { view, q }: { view?: KeywordAgentTermView; q?: string } = {},
): Promise<{ meta: { total: number }; items: KeywordAgentTerm[] }> => {
  const params = new URLSearchParams(loginCustomerParams(loginCustomerId));
  if (view !== undefined) {
    params.set("view", view);
  }
  if (q) {
    params.set("q", q);
  }
  return (
    await axios.get(
      `${makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId)}/terms`,
      { params },
    )
  ).data;
};

export const excludeKeywordAgentTerm = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
  termId: number,
  note: string,
): Promise<KeywordAgentTerm> => {
  return (
    await axios.post(
      `${makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId)}/terms/${termId}/exclude`,
      { note },
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};

export const restoreKeywordAgentTerm = async (
  workspaceSlug: string,
  customerId: string,
  loginCustomerId: string | null,
  campaignId: number,
  adGroupId: number,
  termId: number,
  note: string,
): Promise<KeywordAgentTerm> => {
  return (
    await axios.post(
      `${makeEndpoint(workspaceSlug, customerId, campaignId, adGroupId)}/terms/${termId}/restore`,
      { note },
      { params: loginCustomerParams(loginCustomerId) },
    )
  ).data;
};
